import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import { getUser } from "../utils/storage";
import BottomNav from "../components/BottomNav";

const GoaLeaderboard = () => {
    const navigate = useNavigate();
    const currentUser = getUser();
    const [leaderboard, setLeaderboard] = useState([]);
    const [userStats, setUserStats] = useState({ count: 0, deposits: 0 });
    const [loading, setLoading] = useState(true);

    // 🎲 SEED FAKE DATA - Common Indian Names
    const generateSeedData = (realUserCount) => {
        const fakeNames = [
            "Aarav Sharma", "Vivaan Patel", "Aditya Singh", "Vihaan Rao", "Arjun Malhotra",
            "Sai Gupta", "Ishaan Verma", "Rudra Johar", "Aryan Gandhi", "Kavir Pandey",
            "Ananya Mehra", "Diya Hegde", "Ishita Khan", "Myra Dixit", "Avni Kumar",
            "Prisha Tiwari", "Riya Kapoor", "Anvi Berry", "Kavya Vora", "Sana Shah",
            "Aayush Raj", "Daksh Akhtar", "Reyansh Yadav", "Advait Zinta", "Atharv Dhawan",
            "Ishaan Chand", "Aaryan Bhat", "Kabir Joshi", "Arnav Das", "Aman Reddy"
        ];
        
        // Ensure top 10 always have more than the real user
        const baseCountForTop = Math.max(realUserCount + 5, 20);

        return fakeNames.map((name, index) => {
            let refCount;
            if (index < 10) {
                // Top 10 get high scores to block real user
                refCount = baseCountForTop + (15 - index) + Math.floor(Math.random() * 5);
            } else {
                // Others have random low scores
                refCount = Math.floor(Math.random() * Math.min(realUserCount + 2, 15)) + 2;
            }

            return {
                id: `fake_${index}`,
                first_name: name.split(" ")[0],
                last_name: name.split(" ")[1],
                referral_count: refCount,
                is_fake: true
            };
        });
    };

    useEffect(() => {
        const initData = async () => {
            try {
                // 1. Get Real Stats
                const statsRes = await API.get(`/auth/referral-stats?userId=${currentUser.id}`);
                const realStats = statsRes.data;
                setUserStats(realStats);

                // 2. Generate and Combine
                const fakeData = generateSeedData(parseInt(realStats.count));
                const combined = [
                    ...fakeData,
                    {
                        id: currentUser.id,
                        first_name: currentUser.firstName || "You",
                        last_name: currentUser.lastName || "",
                        referral_count: parseInt(realStats.count),
                        is_current_user: true
                    }
                ];

                // 3. Sort by Referral Count
                combined.sort((a, b) => b.referral_count - a.referral_count);
                
                // ⚠️ DOUBLE CHECK: If user somehow slipped into top 10, push them to 11
                const userIdx = combined.findIndex(u => u.is_current_user);
                if (userIdx < 10) {
                     const userEntry = combined.splice(userIdx, 1)[0];
                     combined.splice(10, 0, userEntry);
                }

                setLeaderboard(combined);
            } catch (err) {
                console.error("Leaderboard Error:", err);
            } finally {
                setLoading(false);
            }
        };

        if (currentUser.id) initData();
    }, [currentUser.id]);

    const userRank = leaderboard.findIndex(u => u.is_current_user) + 1;
    const isUserInTop10 = userRank <= 10;

    return (
        <div className="bg-theme-card text-theme-text min-h-screen pb-32 font-sans relative overflow-x-hidden">
            {/* 🌅 SUNSET HEADER */}
            <div className="relative h-64 bg-gradient-to-br from-orange-400 via-pink-600 to-purple-800 flex flex-col items-center justify-center text-center p-6 shadow-2xl overflow-hidden">
                <div className="absolute top-[-10%] left-[-10%] w-64 h-64 bg-yellow-300 opacity-20 blur-[80px] rounded-full animate-pulse"></div>
                <div className="absolute bottom-[-20%] right-[-10%] w-80 h-80 bg-blue-400 opacity-10 blur-[100px] rounded-full"></div>
                
                <button 
                  onClick={() => navigate(-1)} 
                  className="absolute top-6 left-6 w-10 h-10 bg-theme-bg/20 backdrop-blur-md rounded-full border border-white/20 flex items-center justify-center active:scale-95 transition-all z-20"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 18l-6-6 6-6" />
                    </svg>
                </button>

                <div className="relative z-10">
                    <div className="text-4xl mb-2">🏖️</div>
                    <h1 className="text-3xl font-black italic uppercase tracking-tighter drop-shadow-lg">GOA TRIP</h1>
                    <p className="text-[10px] font-bold tracking-[0.3em] uppercase opacity-80 mt-1">THE ULTIMATE REFERRAL BATTLE</p>
                </div>

                <div className="flex flex-col items-center space-y-2">
                    <div className="mt-4 bg-white/10 backdrop-blur-xl px-4 py-2 rounded-2xl border border-white/10 flex items-center space-x-3 shadow-inner">
                        <span className="text-yellow-400 rotate-12">🏆</span>
                        <span className="text-xs font-black uppercase italic">Top 10 Users Win Best Rewards!</span>
                    </div>
                    <div className="bg-blue-500/20 backdrop-blur-md px-3 py-1.5 rounded-full border border-blue-400/30 flex items-center space-x-2">
                        <span className="text-[9px] font-black uppercase tracking-widest text-blue-200">Winners Announced Every 15 Days! 🎁</span>
                    </div>
                </div>
            </div>

            {/* 📊 LEADERBOARD LIST */}
            <div className="max-w-md mx-auto -mt-6 rounded-t-[3rem] bg-theme-card p-4 relative z-10">
                <div className="flex justify-between items-center mb-6 px-4">
                    <h3 className="text-sm font-black text-theme-text-sec uppercase tracking-widest">Global Rankings</h3>
                    <div className="bg-green-500/10 text-green-400 text-[10px] font-bold px-3 py-1 rounded-full border border-green-500/20">LIVE REFRESH</div>
                </div>

                {loading ? (
                    <div className="py-20 flex flex-col items-center justify-center opacity-40">
                         <div className="w-12 h-12 border-4 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
                         <p className="mt-4 text-[10px] font-black uppercase tracking-widest">Calculating Standings...</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {leaderboard.slice(0, 30).map((user, index) => {
                            const rank = index + 1;
                            const isCurrentUser = user.is_current_user;
                            
                            return (
                                <div 
                                    key={user.id} 
                                    className={`relative p-4 rounded-3xl border flex items-center justify-between transition-all duration-500 ${
                                        isCurrentUser 
                                        ? "bg-gradient-to-r from-emerald-600 to-teal-800 border-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.5)] scale-[1.02] z-20" 
                                        : "bg-theme-card border border-theme-border/40 border-theme-border/50"
                                    }`}
                                >
                                    <div className="flex items-center space-x-4">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-xs ${
                                            rank === 1 ? "bg-yellow-400 text-black shadow-[0_0_10px_#facc15]" :
                                            rank === 2 ? "bg-gray-300 text-black shadow-[0_0_10px_#d1d5db]" :
                                            rank === 3 ? "bg-orange-400 text-black shadow-[0_0_10px_#fb923c]" :
                                            "bg-gray-700 text-theme-text-sec"
                                        }`}>
                                            {rank}
                                        </div>
                                        <div>
                                            <p className={`font-black text-sm tracking-wide ${isCurrentUser ? "text-theme-text" : "text-theme-text"}`}>
                                                {user.first_name} {user.last_name || ""}
                                                {isCurrentUser && <span className="ml-2 text-[8px] bg-white/20 px-2 py-0.5 rounded-full uppercase">YOU</span>}
                                            </p>
                                            <p className={`text-[9px] font-bold ${isCurrentUser ? "text-emerald-100" : "text-theme-text-sec"}`}>Rank Level: PRO PLAYER</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className={`text-lg font-black tracking-tighter ${isCurrentUser ? "text-theme-text" : "text-orange-400"}`}>
                                            {user.referral_count}
                                        </p>
                                        <p className="text-[8px] font-bold uppercase tracking-widest opacity-60">Referrals</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* 📍 STICKY USER RANK CARD AT TOP (Always visible) */}
            {!loading && (
                <div className="sticky top-0 z-50">
                    <div className="bg-gradient-to-r from-emerald-600 to-teal-800 p-4 shadow-2xl border-b border-emerald-400/30 flex items-center justify-between">
                         <div className="flex items-center space-x-4">
                             <div className="w-8 h-8 rounded-full bg-emerald-900 flex items-center justify-center font-black text-xs text-white border border-white/20 shadow-inner">
                                 {userRank}
                             </div>
                             <div>
                                 <p className="font-black text-sm tracking-wide text-white">
                                     {currentUser.firstName} {currentUser.lastName || ""}
                                     <span className="ml-2 text-[8px] bg-white/20 px-2 py-0.5 rounded-full uppercase">YOU</span>
                                 </p>
                                 <p className="text-[9px] font-bold text-emerald-100 uppercase opacity-80">Your Rank: #{userRank}</p>
                             </div>
                         </div>

                         <div className="flex items-center space-x-3">
                            <div className="text-right">
                                <p className="text-lg font-black tracking-tighter text-white">
                                    {userStats.count}
                                </p>
                                <p className="text-[8px] font-bold uppercase tracking-widest opacity-60 text-white">Referrals</p>
                            </div>
                            
                            <button 
                                onClick={() => {
                                    if (navigator.share) {
                                      navigator.share({
                                        title: 'WinroX11 Goa Trip',
                                        text: `I'm on Rank #${userRank} for the Goa Trip Contest! Join WinroX11 using my link and help me win.`,
                                        url: window.location.origin + '?ref=' + currentUser.id
                                      }).catch(() => {});
                                    } else {
                                      alert("Share your link: " + window.location.origin + '?ref=' + currentUser.id);
                                    }
                                }}
                                className="bg-white text-emerald-700 w-10 h-10 rounded-xl flex flex-col items-center justify-center shadow-lg active:scale-90 transition-all border-b-4 border-gray-300"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                                    <polyline points="16 6 12 2 8 6" />
                                    <line x1="12" y1="2" x2="12" y2="15" />
                                </svg>
                                <span className="text-[6px] font-black uppercase mt-0.5">Share</span>
                            </button>
                         </div>
                    </div>
                </div>
            )}

            <BottomNav />
            
            <style dangerouslySetInnerHTML={{ __html: `
                @keyframes slide-up {
                    from { transform: translateY(100px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                .animate-slide-up { animation: slide-up 0.6s cubic-bezier(0.23, 1, 0.32, 1) forwards; }
            `}} />
        </div>
    );
};

export default GoaLeaderboard;
